import { useParams, Navigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Phone, CheckCircle, Star, Quote, Shield, Clock, Banknote, Award, Paintbrush, Layers, Hammer, Ruler, ChevronLeft } from "lucide-react";

import gypsumImg from "@/assets/services/gypsum-board.jpg";
import woodworkImg from "@/assets/services/custom-woodwork.jpg";
import pvcMarbleImg from "@/assets/services/pvc-marble.jpg";
import mirrorsImg from "@/assets/services/decorative-mirrors.jpg";
import shipboardImg from "@/assets/services/shipboard.jpg";
import stoneImg from "@/assets/services/stone-veneer.jpg";
import restorationImg from "@/assets/services/restoration.jpg";
import paintingImg from "@/assets/services/painting-wallpaper.jpg";

const whyChooseUs = [
  { icon: Shield, title: "جودة عالية", desc: "نستخدم أفضل الخامات والمواد العالمية لضمان نتائج تدوم طويلاً" },
  { icon: Clock, title: "سرعة التنفيذ", desc: "نلتزم بالمواعيد المحددة وننجز المشاريع في الوقت المتفق عليه" },
  { icon: Banknote, title: "أسعار تنافسية", desc: "أفضل الأسعار في الرياض مع الحفاظ على أعلى معايير الجودة" },
  { icon: Award, title: "خبرة واحترافية", desc: "فريق عمل متخصص بخبرة تزيد عن 10 سنوات في مجال الديكور والتشطيبات" },
];

const testimonials = [
  { name: "عبدالله المالكي", role: "صاحب فيلا — الرياض", text: "تجربة استثنائية من البداية إلى النهاية. التصميم الداخلي فاق توقعاتنا والتنفيذ كان بجودة عالية جداً.", rating: 5 },
  { name: "فهد العتيبي", role: "مدير مشاريع — شركة عقارية", text: "تعاملنا معهم في ترميم مبنى تجاري وكانت النتائج مبهرة. التزام بالمواعيد وجودة في التنفيذ.", rating: 5 },
  { name: "سارة الدوسري", role: "مصممة ديكور", text: "فريق عمل متميز ومتعاون. نفذوا التصميم بدقة متناهية وأضافوا لمسات إبداعية رائعة.", rating: 5 },
  { name: "محمد الشمري", role: "صاحب شقة — الرياض", text: "أعمال الدهانات وورق الجدران كانت ممتازة. الألوان نابضة بالحياة والتشطيب نظيف جداً.", rating: 5 },
];

interface ServiceData {
  title: string;
  seoTitle: string;
  seoDescription: string;
  heroHeading: string;
  heroSubtext: string;
  image: string;
  alt: string;
  galleryImages: string[];
  features: string[];
  serviceTypes: string[];
  steps: { icon: React.ElementType; title: string; desc: string }[];
  fullDescription: string;
}

const serviceData: Record<string, ServiceData> = {
  "decorations-riyadh": {
    title: "ديكورات وتصميم داخلي",
    seoTitle: "أفضل شركة ديكورات وتشطيبات بالرياض | حسن عبدالله للمقاولات",
    seoDescription: "أفضل خدمات الديكورات والتشطيبات في الرياض — تصاميم فاخرة للفلل والشقق والمكاتب بأعلى جودة وأسعار مناسبة. اتصل الآن!",
    heroHeading: "أفضل شركة ديكورات وتشطيبات بالرياض",
    heroSubtext: "نحوّل مساحتك إلى تحفة فنية — تصاميم داخلية فاخرة بأيدي خبراء متخصصين في الرياض",
    image: woodworkImg,
    alt: "ديكورات وتصميم داخلي فاخر بالرياض",
    galleryImages: [woodworkImg, gypsumImg, pvcMarbleImg, mirrorsImg],
    features: ["تصميم داخلي شامل للفلل والشقق", "تنفيذ ديكورات مودرن وكلاسيك", "اختيار الألوان والخامات المناسبة", "إشراف هندسي متكامل على المشروع", "تصاميم ثلاثية الأبعاد قبل التنفيذ", "ضمان شامل على جميع الأعمال"],
    serviceTypes: ["ديكورات فلل فاخرة", "تصميم داخلي للشقق", "ديكورات مكاتب وشركات", "ديكورات محلات تجارية", "تجديد وتحديث الديكورات القديمة"],
    steps: [
      { icon: Ruler, title: "المعاينة والقياس", desc: "زيارة الموقع وأخذ المقاسات الدقيقة وفهم متطلباتك" },
      { icon: Paintbrush, title: "التصميم والاختيار", desc: "تقديم تصاميم ثلاثية الأبعاد واختيار الخامات والألوان" },
      { icon: Hammer, title: "التنفيذ الاحترافي", desc: "تنفيذ المشروع بأيدي فريق متخصص مع إشراف مستمر" },
      { icon: Layers, title: "التسليم والضمان", desc: "تسليم المشروع بأعلى جودة مع ضمان شامل" },
    ],
    fullDescription: "نقدم في حسن عبدالله للمقاولات والديكورات خدمات تصميم داخلي وديكورات شاملة في الرياض. نحن متخصصون في تحويل المساحات العادية إلى أماكن استثنائية تعكس ذوقك وشخصيتك. فريقنا المتخصص يجمع بين الإبداع في التصميم والدقة في التنفيذ لنقدم لك نتائج تفوق توقعاتك. سواء كنت تبحث عن تصميم مودرن عصري أو كلاسيكي فاخر، نحن هنا لنحقق رؤيتك بأفضل الأسعار.",
  },
  "paint-riyadh": {
    title: "دهانات وورق جدران",
    seoTitle: "أفضل معلم دهانات وورق جدران بالرياض | حسن عبدالله",
    seoDescription: "معلم دهانات وورق جدران بالرياض — دهانات داخلية وخارجية بأجود الماركات العالمية وتركيب ورق جدران بأرقى الخامات وأسعار مناسبة.",
    heroHeading: "أفضل معلم دهانات وورق جدران بالرياض",
    heroSubtext: "دهانات احترافية بأفضل الماركات العالمية — نحوّل جدرانكم إلى لوحات فنية تنبض بالحياة",
    image: paintingImg,
    alt: "دهانات وورق جدران احترافية بالرياض",
    galleryImages: [paintingImg, woodworkImg, gypsumImg, pvcMarbleImg],
    features: ["دهانات داخلية وخارجية بأجود الماركات", "ورق جدران بأحدث التصاميم العالمية", "دهانات تعتيق وتأثيرات خاصة", "دهانات إيبوكسي للأرضيات", "معالجة الجدران والتجهيز الكامل", "ضمان على جميع الأعمال"],
    serviceTypes: ["دهانات جوتن وساير", "دهانات تعتيق وستوكو", "ورق جدران ثلاثي الأبعاد", "ورق جدران كلاسيك ومودرن", "دهانات خارجية مقاومة للعوامل الجوية"],
    steps: [
      { icon: Ruler, title: "معاينة الجدران", desc: "فحص الجدران وتحديد المعالجات اللازمة قبل الدهان" },
      { icon: Paintbrush, title: "اختيار الألوان", desc: "مساعدتك في اختيار الألوان والتشطيبات المناسبة" },
      { icon: Hammer, title: "التجهيز والتنفيذ", desc: "معالجة الجدران وتطبيق الدهان بطبقات احترافية" },
      { icon: Layers, title: "التسليم النهائي", desc: "مراجعة شاملة وتسليم العمل بأعلى جودة" },
    ],
    fullDescription: "نقدم خدمات دهانات احترافية وتركيب ورق جدران بالرياض بأعلى معايير الجودة. نستخدم أفضل الماركات العالمية مثل جوتن وساير لضمان ألوان نابضة بالحياة تدوم لسنوات. فريقنا المتخصص يتعامل مع جميع أنواع الأسطح ويقدم حلول مبتكرة تشمل دهانات التعتيق والستوكو والإيبوكسي. كما نوفر تشكيلة واسعة من ورق الجدران العالمي بتصاميم عصرية وكلاسيكية تناسب جميع الأذواق.",
  },
  "gypsum-riyadh": {
    title: "أعمال الجبس بورد",
    seoTitle: "جبس بورد بالرياض | أسقف وديكورات جبس | حسن عبدالله",
    seoDescription: "أعمال جبس بورد احترافية بالرياض — أسقف معلقة، قواطع ديكورية، إضاءة مخفية، وتصاميم جبسية عصرية بأعلى جودة وأسعار تنافسية.",
    heroHeading: "أفضل معلم جبس بورد بالرياض",
    heroSubtext: "تصاميم جبس بورد عصرية للأسقف والجدران — إضاءة مخفية وأسقف معلقة بأعلى معايير الجودة",
    image: gypsumImg,
    alt: "أعمال جبس بورد عصرية بالرياض",
    galleryImages: [gypsumImg, woodworkImg, mirrorsImg, paintingImg],
    features: ["أسقف معلقة وإضاءة مخفية", "قواطع ديكورية للمساحات", "تصاميم جبسية حديثة ومبتكرة", "عزل صوتي وحراري", "أسقف مستعارة للمكاتب", "تنفيذ سريع بجودة عالية"],
    serviceTypes: ["أسقف جبس بورد معلقة", "قواطع جبس بورد ديكورية", "إضاءة مخفية LED", "أسقف مستعارة 60×60", "ديكورات جبسية للجدران"],
    steps: [
      { icon: Ruler, title: "التصميم والتخطيط", desc: "رسم التصميم المطلوب وتحديد نقاط الإضاءة والقياسات" },
      { icon: Layers, title: "تركيب الهيكل", desc: "تثبيت الهيكل المعدني بدقة وفق المعايير الهندسية" },
      { icon: Hammer, title: "تركيب الألواح", desc: "تركيب ألواح الجبس بورد ومعالجة الفواصل" },
      { icon: Paintbrush, title: "التشطيب النهائي", desc: "المعجنة والصنفرة والدهان للحصول على سطح مثالي" },
    ],
    fullDescription: "نقدم أعمال جبس بورد احترافية في الرياض تشمل الأسقف المعلقة والقواطع الديكورية والإضاءة المخفية. فريقنا المتخصص ينفذ تصاميم جبسية عصرية تجمع بين الجمال والوظيفة. نستخدم أفضل أنواع ألواح الجبس بورد المقاومة للرطوبة والحريق، مع اهتمام بأدق التفاصيل في التشطيب لضمان نتائج مثالية تدوم لسنوات طويلة.",
  },
  "wallpaper-riyadh": {
    title: "ورق جدران فاخر",
    seoTitle: "تركيب ورق جدران بالرياض | ورق جدران 3D | حسن عبدالله",
    seoDescription: "تركيب ورق جدران بالرياض بأحدث التصاميم العالمية — ورق جدران ثلاثي الأبعاد وكلاسيك ومودرن بأسعار تنافسية وتركيب احترافي.",
    heroHeading: "أفضل تركيب ورق جدران بالرياض",
    heroSubtext: "ورق جدران فاخر بتصاميم عالمية — تركيب احترافي يحوّل جدرانك إلى لوحات فنية مذهلة",
    image: paintingImg,
    alt: "تركيب ورق جدران فاخر بالرياض",
    galleryImages: [paintingImg, woodworkImg, gypsumImg, mirrorsImg],
    features: ["ورق جدران ثلاثي الأبعاد (3D)", "تصاميم كلاسيك ومودرن متنوعة", "خامات مقاومة للرطوبة والحرارة", "تركيب احترافي وسريع", "ورق جدران قابل للتنظيف", "ضمان على التركيب والخامات"],
    serviceTypes: ["ورق جدران ثلاثي الأبعاد", "ورق جدران فينيل مقاوم للماء", "ورق جدران قماشي فاخر", "ورق جدران طبيعي (خيزران وحرير)", "ورق جدران مخصص بالطلب"],
    steps: [
      { icon: Paintbrush, title: "اختيار التصميم", desc: "تصفح مئات التصاميم واختيار الأنسب لمساحتك" },
      { icon: Ruler, title: "القياس والتحضير", desc: "قياس الجدران وتجهيز الأسطح للتركيب" },
      { icon: Hammer, title: "التركيب الاحترافي", desc: "تركيب ورق الجدران بدقة متناهية بدون فقاعات أو عيوب" },
      { icon: Layers, title: "التفتيش والتسليم", desc: "فحص شامل والتأكد من جودة التركيب" },
    ],
    fullDescription: "نوفر خدمة تركيب ورق جدران احترافية في الرياض بأحدث التصاميم العالمية. تشكيلتنا الواسعة تشمل ورق جدران ثلاثي الأبعاد وفينيل مقاوم للماء وأقمشة فاخرة ومواد طبيعية. فريقنا المحترف يضمن تركيبًا مثاليًا بدون فقاعات أو عيوب، مع الاهتمام بأدق التفاصيل لضمان نتيجة تدوم لسنوات. نقدم استشارات مجانية لمساعدتك في اختيار التصميم الأنسب لمنزلك.",
  },
  "parquet-riyadh": {
    title: "تركيب باركيه وأرضيات",
    seoTitle: "تركيب باركيه وأرضيات بالرياض | حسن عبدالله للمقاولات",
    seoDescription: "تركيب باركيه وأرضيات خشبية وبديل خشب بالرياض — أرضيات فاخرة بتشطيبات عالية الجودة وأسعار مناسبة. اتصل الآن!",
    heroHeading: "أفضل تركيب باركيه وأرضيات بالرياض",
    heroSubtext: "أرضيات فاخرة بتشطيبات مثالية — باركيه خشب طبيعي وبديل خشب بأعلى جودة وأسعار تنافسية",
    image: shipboardImg,
    alt: "تركيب باركيه وأرضيات خشبية بالرياض",
    galleryImages: [shipboardImg, woodworkImg, stoneImg, pvcMarbleImg],
    features: ["باركيه خشب طبيعي فاخر", "أرضيات بديل الخشب (SPC/WPC)", "أرضيات فينيل عالية الجودة", "تركيب احترافي ومتين", "عزل صوتي تحت الأرضيات", "ضمان شامل على التركيب"],
    serviceTypes: ["باركيه خشب طبيعي", "أرضيات بديل خشب SPC", "أرضيات فينيل لاصق", "أرضيات لامينيت", "أرضيات خارجية مقاومة للعوامل الجوية"],
    steps: [
      { icon: Ruler, title: "المعاينة والقياس", desc: "فحص الأرضية الحالية وأخذ المقاسات الدقيقة" },
      { icon: Layers, title: "تجهيز الأرضية", desc: "تسوية الأرضية وتركيب العازل الصوتي والحراري" },
      { icon: Hammer, title: "التركيب الاحترافي", desc: "تركيب الأرضيات بدقة مع الاهتمام بالتفاصيل" },
      { icon: Paintbrush, title: "التشطيب والتسليم", desc: "تركيب الزوايا والوزرات وتسليم العمل بأفضل صورة" },
    ],
    fullDescription: "نقدم خدمات تركيب باركيه وأرضيات متكاملة في الرياض تشمل الباركيه الخشب الطبيعي وبديل الخشب والفينيل واللامينيت. نستخدم أفضل الخامات العالمية ونوفر تشكيلة واسعة من الألوان والتصاميم. فريقنا المتخصص يضمن تركيبًا احترافيًا يدوم لسنوات، مع تركيب العوازل الصوتية والحرارية لراحتك. نقدم ضمانًا شاملاً على جميع أعمال التركيب.",
  },
  "pvc-marble-riyadh": {
    title: "بديل الرخام وبديل الخشب",
    seoTitle: "بديل رخام وبديل خشب بالرياض | حسن عبدالله للمقاولات",
    seoDescription: "تركيب بديل رخام وبديل خشب بالرياض — ألواح PVC وWPC بتصاميم فاخرة مقاومة للماء والحرارة بأسعار تنافسية.",
    heroHeading: "أفضل تركيب بديل رخام وبديل خشب بالرياض",
    heroSubtext: "ألواح بديل رخام وبديل خشب بتصاميم مودرن فاخرة — مقاومة للماء والحرارة، سهلة التركيب والصيانة",
    image: pvcMarbleImg,
    alt: "تركيب بديل رخام وبديل خشب بالرياض",
    galleryImages: [pvcMarbleImg, stoneImg, woodworkImg, mirrorsImg],
    features: ["ألواح بديل رخام بتصاميم طبيعية", "بديل خشب WPC مقاوم للرطوبة", "لوفرز خشبي مودرن", "تركيب سريع بدون فوضى", "سهولة التنظيف والصيانة", "ضمان شامل على المنتجات"],
    serviceTypes: ["بديل رخام PVC للجدران", "بديل خشب WPC لوفرز", "ألواح ديكورية ثلاثية الأبعاد", "كسوة أعمدة ومداخل", "تغليف جدران كاملة"],
    steps: [
      { icon: Ruler, title: "المعاينة والقياس", desc: "زيارة الموقع وأخذ المقاسات الدقيقة واختيار التصميم" },
      { icon: Paintbrush, title: "اختيار الخامة", desc: "اختيار نوع البديل والألوان المناسبة لمساحتك" },
      { icon: Hammer, title: "التركيب الاحترافي", desc: "تركيب الألواح بدقة عالية وتشطيب نظيف" },
      { icon: Layers, title: "التسليم والضمان", desc: "تسليم المشروع مع ضمان على الخامات والتركيب" },
    ],
    fullDescription: "نقدم خدمات تركيب بديل الرخام وبديل الخشب بالرياض بأحدث التصاميم العالمية. ألواح PVC بديل الرخام توفر مظهر الرخام الطبيعي بوزن خفيف وسعر اقتصادي ومقاومة عالية للماء والرطوبة. كما نوفر ألواح WPC بديل الخشب واللوفرز بتشكيلة واسعة من الألوان والتصاميم المودرن. جميع منتجاتنا سهلة التنظيف ومقاومة للخدش مع ضمان شامل.",
  },
  "mirrors-riyadh": {
    title: "تركيب مرايات ديكور",
    seoTitle: "تركيب مرايات ديكور بالرياض | حسن عبدالله للمقاولات",
    seoDescription: "تركيب مرايات ديكورية بالرياض — مرايات حائطية ومداخل وتصاميم مخصصة بأعلى جودة وأسعار مناسبة.",
    heroHeading: "أفضل تركيب مرايات ديكور بالرياض",
    heroSubtext: "مرايات ديكورية بتصاميم حديثة وأنيقة — نضيف لمساحتك اتساعًا وفخامة",
    image: mirrorsImg,
    alt: "تركيب مرايات ديكورية بالرياض",
    galleryImages: [mirrorsImg, woodworkImg, gypsumImg, pvcMarbleImg],
    features: ["مرايات حائطية بمقاسات مخصصة", "مرايات مداخل فاخرة", "مرايات بإضاءة LED مدمجة", "مرايات ديكورية بإطارات ذهبية", "تركيب آمن ومتين", "ضمان على التركيب والمنتج"],
    serviceTypes: ["مرايات حائطية كبيرة", "مرايات مداخل ديكورية", "مرايات حمامات مع إضاءة", "مرايات أرضية قائمة", "مرايات مخصصة بأشكال هندسية"],
    steps: [
      { icon: Ruler, title: "القياس والتصميم", desc: "أخذ المقاسات وتحديد التصميم والشكل المطلوب" },
      { icon: Paintbrush, title: "اختيار النوع", desc: "اختيار نوع المرآة والإطار والإضاءة" },
      { icon: Hammer, title: "التركيب الآمن", desc: "تركيب احترافي بمواد تثبيت قوية وآمنة" },
      { icon: Layers, title: "التسليم النهائي", desc: "تنظيف وتلميع وتسليم بأفضل صورة" },
    ],
    fullDescription: "نقدم خدمات تركيب مرايات ديكورية احترافية في الرياض. نوفر تشكيلة واسعة من المرايات الحائطية ومرايات المداخل والمرايات المزودة بإضاءة LED. فريقنا المتخصص يضمن تركيبًا آمنًا ومتينًا باستخدام أفضل مواد التثبيت. المرايات الديكورية تضيف اتساعًا وفخامة لأي مساحة وتعكس الإضاءة بشكل جميل.",
  },
  "shipboard-riyadh": {
    title: "شيبورد",
    seoTitle: "تركيب شيبورد بالرياض | حسن عبدالله للمقاولات والديكورات",
    seoDescription: "تركيب ألواح شيبورد بالرياض — حلول عملية وجمالية للجدران والأسقف بأسعار تنافسية وتركيب احترافي.",
    heroHeading: "أفضل تركيب شيبورد بالرياض",
    heroSubtext: "ألواح شيبورد عالية الجودة — حلول عملية وجمالية للجدران والأسقف بأسعار تنافسية",
    image: shipboardImg,
    alt: "تركيب شيبورد بالرياض",
    galleryImages: [shipboardImg, woodworkImg, pvcMarbleImg, stoneImg],
    features: ["ألواح شيبورد بتصاميم متنوعة", "مقاومة للرطوبة والحشرات", "عزل صوتي وحراري", "تركيب سريع ونظيف", "صيانة سهلة ومنخفضة التكلفة", "ضمان على الخامات والتركيب"],
    serviceTypes: ["شيبورد جدران داخلية", "شيبورد أسقف ديكورية", "شيبورد واجهات خارجية", "شيبورد قواطع ديكورية", "شيبورد مخصص بمقاسات خاصة"],
    steps: [
      { icon: Ruler, title: "المعاينة والتخطيط", desc: "معاينة الموقع وتحديد التصميم والمساحات" },
      { icon: Paintbrush, title: "اختيار التصميم", desc: "اختيار اللون والنقشة من تشكيلتنا الواسعة" },
      { icon: Hammer, title: "التركيب", desc: "تركيب الألواح بدقة واحترافية عالية" },
      { icon: Layers, title: "التشطيب والتسليم", desc: "تشطيب الزوايا والحواف وتسليم المشروع" },
    ],
    fullDescription: "نقدم خدمات تركيب ألواح شيبورد احترافية في الرياض للجدران والأسقف والواجهات. الشيبورد خيار عملي وجمالي يتميز بمقاومته للرطوبة والحشرات وسهولة صيانته. نوفر تشكيلة واسعة من الألوان والتصاميم بأسعار تنافسية مع ضمان شامل على التركيب والخامات.",
  },
  "stone-veneer-riyadh": {
    title: "صفائح حجرية مودرن",
    seoTitle: "صفائح حجرية مودرن بالرياض | حسن عبدالله للمقاولات",
    seoDescription: "تركيب صفائح حجرية مودرن بالرياض — صفائح رقيقة بتصاميم عصرية للجدران الداخلية والخارجية بوزن خفيف وأسعار مناسبة.",
    heroHeading: "أفضل تركيب صفائح حجرية مودرن بالرياض",
    heroSubtext: "صفائح حجرية رقيقة بتصاميم عصرية — فخامة الحجر الطبيعي بوزن خفيف وتركيب سهل",
    image: stoneImg,
    alt: "صفائح حجرية مودرن بالرياض",
    galleryImages: [stoneImg, pvcMarbleImg, woodworkImg, mirrorsImg],
    features: ["صفائح حجرية طبيعية رقيقة", "وزن خفيف وسهلة التركيب", "مقاومة للعوامل الجوية", "تصاميم فريدة لا تتكرر", "مناسبة للداخل والخارج", "صيانة منخفضة التكلفة"],
    serviceTypes: ["صفائح حجرية للجدران الداخلية", "صفائح حجرية للواجهات الخارجية", "كسوة أعمدة حجرية", "خلفيات تلفزيون حجرية", "ديكور مداخل حجري"],
    steps: [
      { icon: Ruler, title: "المعاينة والاختيار", desc: "زيارة الموقع واختيار نوع الحجر والتصميم" },
      { icon: Paintbrush, title: "تجهيز الأسطح", desc: "تجهيز الجدران وتحضيرها لاستقبال الصفائح" },
      { icon: Hammer, title: "التركيب الاحترافي", desc: "تركيب الصفائح بمواد لاصقة قوية ومتينة" },
      { icon: Layers, title: "التشطيب والمعالجة", desc: "معالجة الفواصل وتشطيب نهائي أنيق" },
    ],
    fullDescription: "نقدم خدمات تركيب صفائح حجرية مودرن في الرياض. هذه الصفائح الرقيقة مصنوعة من حجر طبيعي حقيقي بسماكة ملليمترات قليلة مما يجعلها خفيفة الوزن وسهلة التركيب. كل قطعة فريدة بتصميمها الطبيعي مما يضيف لمسة فخامة استثنائية لمساحتك. مناسبة للاستخدام الداخلي والخارجي مع مقاومة عالية للعوامل الجوية.",
  },
  "restoration-riyadh": {
    title: "ترميم ومقاولات عامة",
    seoTitle: "ترميم مباني ومقاولات عامة بالرياض | حسن عبدالله",
    seoDescription: "خدمات ترميم مباني وفلل ومقاولات عامة بالرياض — أعمال هيكلية وتشطيبات بإشراف هندسي متخصص وضمان على الجودة.",
    heroHeading: "أفضل شركة ترميم ومقاولات عامة بالرياض",
    heroSubtext: "ترميم شامل للمباني والفلل — أعمال هيكلية وتشطيبات بإشراف هندسي متخصص",
    image: restorationImg,
    alt: "ترميم مباني ومقاولات عامة بالرياض",
    galleryImages: [restorationImg, gypsumImg, paintingImg, woodworkImg],
    features: ["ترميم هيكلي شامل للمباني", "تجديد واجهات المباني القديمة", "إصلاح التشققات والتسربات", "تشطيبات داخلية وخارجية", "إشراف هندسي متكامل", "ضمان شامل على جميع الأعمال"],
    serviceTypes: ["ترميم فلل ومنازل", "ترميم مباني تجارية", "تجديد واجهات خارجية", "إصلاح أعمال السباكة والكهرباء", "مقاولات عامة وبناء"],
    steps: [
      { icon: Ruler, title: "الكشف والتقييم", desc: "معاينة المبنى وتقييم حالته وتحديد الأعمال المطلوبة" },
      { icon: Layers, title: "التخطيط والتصميم", desc: "إعداد خطة العمل والجدول الزمني والميزانية" },
      { icon: Hammer, title: "التنفيذ والإشراف", desc: "تنفيذ الأعمال بإشراف هندسي مستمر" },
      { icon: Paintbrush, title: "التسليم والضمان", desc: "تسليم المشروع مع ضمان شامل على الجودة" },
    ],
    fullDescription: "نقدم خدمات ترميم ومقاولات عامة شاملة في الرياض تشمل الترميم الهيكلي وتجديد الواجهات وإصلاح التسربات والتشققات. فريقنا الهندسي المتخصص يشرف على جميع مراحل العمل لضمان أعلى معايير الجودة والسلامة. نتعامل مع جميع أنواع المباني من فلل ومنازل ومباني تجارية مع الالتزام بالمواعيد والميزانية المحددة.",
  },
};
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ServiceDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = slug ? serviceData[slug] : null;

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <div className="min-h-screen">
      <SEOHead
        title={service.seoTitle}
        description={service.seoDescription}
        canonical={`/services/${slug}`}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.title,
          description: service.seoDescription,
          provider: {
            "@type": "LocalBusiness",
            name: "حسن عبدالله للمقاولات والديكورات",
            telephone: "+966556641963",
            address: { "@type": "PostalAddress", addressLocality: "الرياض", addressCountry: "SA" },
          },
          areaServed: { "@type": "City", name: "الرياض" },
        }}
      />
      <Header />

      <div className="pt-20">
        {/* 1. Hero Section */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
          <img src={service.image} alt={service.alt} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/80 to-charcoal/40" />
          <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-marble mb-6 leading-tight"
            >
              {service.heroHeading}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-marble/80 text-lg md:text-xl max-w-2xl mx-auto mb-10"
            >
              {service.heroSubtext}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-dark font-bold px-10 py-7 text-lg gold-shadow rounded-xl">
                <a href="tel:0556641963" className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  اتصل الآن
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-gold text-gold hover:bg-gold/10 px-10 py-7 text-lg rounded-xl font-bold">
                <a href="https://wa.me/966556641963" target="_blank" rel="noopener noreferrer">
                  واتساب ١
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-gold text-gold hover:bg-gold/10 px-10 py-7 text-lg rounded-xl font-bold">
                <a href="https://wa.me/966534859731" target="_blank" rel="noopener noreferrer">
                  واتساب ٢
                </a>
              </Button>
            </motion.div>
          </div>
        </section>

        {/* 2. Why Choose Us */}
        <section className="section-padding bg-background">
          <div className="container mx-auto max-w-6xl">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
              <div className="w-12 h-0.5 gold-gradient mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">لماذا تختار حسن عبدالله؟</h2>
              <p className="text-muted-foreground max-w-xl mx-auto text-lg">نقدم لك أعلى معايير الجودة بأسعار تنافسية</p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChooseUs.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="text-center p-6 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="w-14 h-14 rounded-xl gold-gradient flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-primary-foreground" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 3. Service Details */}
        <section className="section-padding bg-secondary text-secondary-foreground">
          <div className="container mx-auto max-w-6xl">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
              <div className="w-12 h-0.5 gold-gradient mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">تفاصيل الخدمة</h2>
              <p className="text-secondary-foreground/60 max-w-2xl mx-auto text-lg">{service.fullDescription}</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
              {/* What we offer */}
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <h3 className="text-xl font-bold mb-6 text-secondary-foreground">ما نقدمه لك</h3>
                <div className="space-y-3">
                  {service.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3 p-4 rounded-xl bg-card/5 border border-border/10">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-secondary-foreground/90">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Service types */}
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.1 }}>
                <h3 className="text-xl font-bold mb-6 text-secondary-foreground">أنواع الخدمة</h3>
                <div className="space-y-3">
                  {service.serviceTypes.map((type) => (
                    <div key={type} className="flex items-center gap-3 p-4 rounded-xl bg-card/5 border border-border/10">
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                      <span className="text-secondary-foreground/90">{type}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Steps */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <h3 className="text-xl font-bold mb-8 text-center text-secondary-foreground">خطوات التنفيذ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {service.steps.map((step, i) => {
                  const StepIcon = step.icon;
                  return (
                    <div key={step.title} className="relative text-center p-6 rounded-2xl bg-card/5 border border-border/10">
                      <div className="absolute -top-3 right-4 w-7 h-7 rounded-full gold-gradient flex items-center justify-center text-sm font-bold text-primary-foreground">
                        {i + 1}
                      </div>
                      <StepIcon className="w-8 h-8 text-primary mx-auto mb-3" />
                      <h4 className="font-bold text-secondary-foreground mb-2">{step.title}</h4>
                      <p className="text-secondary-foreground/60 text-sm">{step.desc}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. Gallery */}
        <section className="section-padding bg-background">
          <div className="container mx-auto max-w-6xl">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
              <div className="w-12 h-0.5 gold-gradient mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">من أعمالنا</h2>
              <p className="text-muted-foreground max-w-xl mx-auto text-lg">نماذج حقيقية من مشاريعنا المنفذة بالرياض</p>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {service.galleryImages.map((img, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative aspect-square rounded-2xl overflow-hidden group"
                >
                  <img src={img} alt={`${service.title} - عمل ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/30 transition-colors duration-300" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. Pricing Section */}
        <section className="section-padding bg-card">
          <div className="container mx-auto max-w-4xl text-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <div className="w-12 h-0.5 gold-gradient mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">أسعار تنافسية</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
                نقدم أعلى معايير الجودة بأسعار مناسبة تنافس السوق — احصل على عرض سعر مجاني الآن
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                {[
                  { label: "استشارة مجانية", desc: "معاينة الموقع وتقديم المشورة بدون أي رسوم" },
                  { label: "عرض سعر مفصل", desc: "عرض سعر شامل وواضح بدون تكاليف مخفية" },
                  { label: "ضمان على الأعمال", desc: "ضمان شامل على جميع الأعمال المنفذة" },
                ].map((item) => (
                  <div key={item.label} className="p-6 rounded-2xl bg-background border border-border">
                    <h3 className="font-bold text-foreground text-lg mb-2">{item.label}</h3>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-dark font-bold px-12 py-7 text-lg gold-shadow rounded-xl">
                  <a href="https://wa.me/966556641963" target="_blank" rel="noopener noreferrer">
                    واتساب ١ - عرض سعر
                  </a>
                </Button>
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-dark font-bold px-12 py-7 text-lg gold-shadow rounded-xl">
                  <a href="https://wa.me/966534859731" target="_blank" rel="noopener noreferrer">
                    واتساب ٢ - عرض سعر
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 6. Testimonials */}
        <section className="section-padding charcoal-gradient">
          <div className="container mx-auto max-w-4xl">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
              <div className="w-12 h-0.5 gold-gradient mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-marble mb-4">آراء عملائنا</h2>
              <p className="text-marble/70 max-w-lg mx-auto">ثقة عملائنا هي أغلى ما نملك</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-card/5 border border-border/10"
                >
                  <Quote className="w-8 h-8 text-primary/30 mb-4" />
                  <p className="text-marble/90 leading-relaxed mb-4">"{t.text}"</p>
                  <div className="flex gap-1 mb-2">
                    {Array.from({ length: t.rating }).map((_, s) => (
                      <Star key={s} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <h4 className="text-marble font-bold">{t.name}</h4>
                  <span className="text-marble/60 text-sm">{t.role}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Final CTA */}
        <section className="section-padding bg-background">
          <div className="container mx-auto max-w-4xl text-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">جاهز لتحويل مساحتك؟</h2>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-10">
                تواصل معنا الآن واحصل على استشارة مجانية وعرض سعر خاص
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-dark font-bold px-12 py-7 text-lg gold-shadow rounded-xl">
                  <a href="tel:0556641963" className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    اتصل الآن
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/10 px-12 py-7 text-lg rounded-xl font-bold">
                  <a href="https://wa.me/966556641963" target="_blank" rel="noopener noreferrer">
                    واتساب ١
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/10 px-12 py-7 text-lg rounded-xl font-bold">
                  <a href="https://wa.me/966534859731" target="_blank" rel="noopener noreferrer">
                    واتساب ٢
                  </a>
                </Button>
              </div>
              <div className="mt-8">
                <Link to="/services" className="inline-flex items-center gap-2 text-primary hover:text-gold-dark font-semibold transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                  العودة لجميع الخدمات
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ServiceDetailPage;
